import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Trash2 } from "lucide-react"
import React from "react"
import type { SchemaScoreField } from "@/api/types/schema"

interface ScoreFieldEditorProps {
  score: SchemaScoreField
  sectionUuid: string
  onUpdate: (sectionUuid: string, scoreId: string, updates: Partial<SchemaScoreField>) => void
  onRemove: (sectionUuid: string, scoreId: string) => void
  isLoading?: boolean
}

export const ScoreFieldEditor = React.memo(({ 
  score, 
  sectionUuid, 
  onUpdate, 
  onRemove, 
  isLoading 
}: ScoreFieldEditorProps) => {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
      <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Score ID</Label>
          <Input
            value={score.id}
            onChange={(e) => onUpdate(sectionUuid, score.id, { id: e.target.value })}
            placeholder="score_id"
            className="font-mono text-sm h-8"
            disabled={isLoading}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Score Title</Label>
          <Input
            value={score.title}
            onChange={(e) => onUpdate(sectionUuid, score.id, { title: e.target.value })}
            placeholder="Score Title"
            className="text-sm h-8"
            disabled={isLoading}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Min</Label>
          <Input
            type="number"
            value={score.min ?? 1}
            onChange={(e) => {
              const num_val = e.target.value === '' ? 1 : parseInt(e.target.value, 10)
              if (!isNaN(num_val)) {
                onUpdate(sectionUuid, score.id, { min: num_val })
              }
            }}
            className="text-sm h-8"
            disabled={isLoading}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Max</Label>
          <Input
            type="number"
            value={score.max ?? 10}
            onChange={(e) => {
              const num_val = e.target.value === '' ? 10 : parseInt(e.target.value, 10)
              if (!isNaN(num_val)) {
                onUpdate(sectionUuid, score.id, { max: num_val })
              }
            }}
            className="text-sm h-8"
            disabled={isLoading}
          />
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onRemove(sectionUuid, score.id)}
        disabled={isLoading}
        title="Remove Score"
      >
        <Trash2 className="h-4 w-4 text-red-500" />
      </Button>
    </div>
  )
})

ScoreFieldEditor.displayName = 'ScoreFieldEditor'
